import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Event as EventIcon } from "@mui/icons-material";
import api from "../../api";

const POLL_MS = 60000;

const STATUS = {
  ongoing: { label: "On now", color: "success" },
  pending: { label: "Upcoming", color: "primary" },
  ended: { label: "Ended", color: "default" },
};

const formatDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const formatTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "");

/**
 * A parent's read-only view of their child's live class timetable. Only the student can
 * join a class, so there is no Join button here.
 */
export default function ChildClasses() {
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState("");
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [tab, setTab] = useState("upcoming");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("parent/students/names")
      .then((res) => {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setChildren(list);
        if (list.length) setChildId(list[0].id);
        else setLoading(false);
      })
      .catch(() => {
        setError("Could not load your students.");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!childId) return undefined;
    let cancelled = false;
    const load = async (initial) => {
      if (initial) setLoading(true);
      try {
        const res = await api.get(`parent/children/${childId}/classes`);
        if (!cancelled) setData(res.data?.data || { upcoming: [], past: [] });
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || "Could not load classes.");
      } finally {
        if (!cancelled && initial) setLoading(false);
      }
    };
    load(true);
    const t = setInterval(() => load(false), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [childId]);

  const rows = tab === "upcoming" ? data.upcoming : data.past;
  const childName = children.find((c) => Number(c.id) === Number(childId))?.full_name;

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Classes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {childName ? `${childName}'s live class timetable.` : "Your child's live class timetable."} You'll also get a
            reminder before each class.
          </Typography>
        </Box>
        {children.length > 1 && (
          <Select size="small" value={childId} onChange={(e) => setChildId(e.target.value)} sx={{ minWidth: 220 }}>
            {children.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.full_name}
              </MenuItem>
            ))}
          </Select>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="upcoming" label={`Upcoming (${data.upcoming.length})`} />
        <Tab value="past" label="Past" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !children.length ? (
        <Alert severity="info">Add a student to your account to see their classes.</Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info">{tab === "upcoming" ? "No upcoming classes scheduled." : "No past classes yet."}</Alert>
      ) : (
        <Box sx={{ display: "grid", gap: 1.5 }}>
          {rows.map((c) => {
            const status = STATUS[c.status] || STATUS.pending;
            return (
              <Card key={c.id} variant="outlined">
                <CardContent sx={{ display: "flex", gap: 2, alignItems: "flex-start", "&:last-child": { pb: 2 } }}>
                  <EventIcon color={c.status === "ongoing" ? "success" : "primary"} sx={{ mt: 0.5 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography fontWeight={700}>{c.name}</Typography>
                      <Chip size="small" label={status.label} color={status.color} />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {formatDateTime(c.start_time)}
                      {c.end_time ? ` – ${formatTime(c.end_time)}` : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[c.group?.name, c.course?.name, c.tutor?.full_name ? `Tutor: ${c.tutor.full_name}` : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                    {c.schedule_summary && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {c.schedule_summary}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
